/**
 * usePlanUsage — single-source-of-truth for the dashboard quota meters.
 *
 * Combines TWO server endpoints into one cohesive shape so widgets
 * don't each have to coordinate their own loading state:
 *
 *   1. GET /pages/limits          → plan + storage / submissions / pages
 *   2. GET /safe-pages/ai/usage   → AI Safe Page generations meter
 *
 * Why two requests instead of one merged endpoint: the AI usage route
 * lives in its own service (services/aiSafePageQuota.js) with its own
 * monthly counter table, and merging it into /pages/limits would
 * couple two otherwise unrelated quota systems. Cheaper to fetch in
 * parallel than to refactor the backend mid-Phase-7.
 *
 * The AI fetch is allowed to fail silently — Free/Pro tiers don't
 * have AI Safe Pages at all, and the 4xx is expected. Failing the
 * whole hook would gate the storage / submissions cards on a request
 * that isn't even relevant to those tiers.
 *
 * Refresh policy:
 *   - on mount
 *   - every 60s (matches NotificationBell's poll cadence)
 *   - on window focus (so coming back from a builder upload reflects
 *     the new storage number without a full reload)
 *
 * 401 handling: bubbles up to the existing global apiInterceptor —
 * we just leave state at "not loaded" and don't throw, so the
 * dashboard keeps rendering its other widgets. Calling logout()
 * here would race the interceptor.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const REFRESH_INTERVAL_MS = 60_000;

export type PlanTier = 'free' | 'pro' | 'agency' | 'enterprise';

interface PagesUsage {
  used: number;
  max: number;
  // Phase 4: separate base vs effective so widgets can render
  // "5 + 2 add-on slots" annotations without re-doing the math.
  baseMax: number;
  extraFromAddons: number;
}

interface StorageUsage {
  usedMb: number;
  capMb: number;
  baseCapMb: number;
  packMb: number;
}

interface SubmissionsUsage {
  used: number;
  cap: number;
}

interface CustomDomainsUsage {
  used: number;
  // -1 = unlimited (Enterprise), 0 = not available on this plan (Free).
  cap: number;
}

interface SeatsUsage {
  used: number;
  // -1 = unlimited (Enterprise), 0 = collaboration disabled (Free).
  max: number;
}

interface AiUsage {
  used: number;
  limit: number;
  unlimited: boolean;
  resetAt: string | null;
}

interface AddonsSummary {
  pageSlotsExtra: number;
  domainSlotsExtra: number;
  storagePackMb: number;
  aiGenPackRemaining: number;
}

export interface PlanUsageState {
  isLoading: boolean;
  error: string | null;
  plan: PlanTier | null;
  pages: PagesUsage | null;
  storage: StorageUsage | null;
  submissions: SubmissionsUsage | null;
  ai: AiUsage | null;
  customDomains: CustomDomainsUsage | null;
  seats: SeatsUsage | null;
  addons: AddonsSummary | null;
  retentionDays: number | null;
  grandfatheredUntil: string | null;
  refresh: () => Promise<void>;
}

interface PagesLimitsResponse {
  plan: string;
  limits: { maxPages: number };
  quotas: { analyticsRetentionDays?: number };
  usage: {
    pages: number;
    maxPages: number;
    maxPagesEffective?: number;
    pageSlotsExtra?: number;
    storageUsedMb: number;
    storageCapMb: number;
    storageCapMbEffective?: number;
    storagePackMb?: number;
    submissionsUsed: number;
    submissionsCap: number;
    customDomainSlotsExtra?: number;
    aiGenPackRemaining?: number;
  };
  grandfatheredUntil: string | null;
}

interface AiUsageResponse {
  plan: string;
  limit: number;
  used: number;
  remaining: number;
  unlimited?: boolean;
  resetAt?: string | null;
}

interface CustomDomainsResponse {
  used?: number;
  cap?: number;
}

interface TeamMembersResponse {
  seats?: { used?: number; max?: number };
}

function authHeader(): HeadersInit {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function normalizePlan(raw: string | null | undefined): PlanTier {
  if (raw === 'plus') return 'pro';
  if (raw === 'pro' || raw === 'agency' || raw === 'enterprise' || raw === 'free') return raw;
  return 'free';
}

export function usePlanUsage(): PlanUsageState {
  const [state, setState] = useState<Omit<PlanUsageState, 'refresh'>>({
    isLoading: true,
    error: null,
    plan: null,
    pages: null,
    storage: null,
    submissions: null,
    ai: null,
    customDomains: null,
    seats: null,
    addons: null,
    retentionDays: null,
    grandfatheredUntil: null,
  });

  // We use a ref so the polling interval can read the latest fetcher
  // without re-installing on each render — preserves the 60s cadence
  // even when other state in the hook updates.
  const isMountedRef = useRef(true);

  const fetchAll = useCallback(async () => {
    const headers = authHeader();
    if (!('Authorization' in headers)) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }

    try {
      // custom-domains + team/members are best-effort like the AI probe:
      // a member viewing someone else's workspace (or a Free tier with no
      // seats) can legitimately 403/return empty, and that must not gate
      // the storage/AI cards.
      const [limitsRes, aiRes, domainsRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/pages/limits`, { headers }),
        fetch(`${API_URL}/safe-pages/ai/usage`, { headers }).catch(() => null),
        fetch(`${API_URL}/custom-domains`, { headers }).catch(() => null),
        fetch(`${API_URL}/team/members`, { headers }).catch(() => null),
      ]);

      if (!limitsRes.ok) {
        if (limitsRes.status === 401) {
          if (isMountedRef.current) {
            setState((s) => ({ ...s, isLoading: false }));
          }
          return;
        }
        throw new Error(`limits ${limitsRes.status}`);
      }

      const limits = (await limitsRes.json()) as PagesLimitsResponse;

      let ai: AiUsage | null = null;
      if (aiRes && aiRes.ok) {
        const aiJson = (await aiRes.json()) as AiUsageResponse;
        ai = {
          used: Number(aiJson.used) || 0,
          limit: Number(aiJson.limit) || 0,
          unlimited: aiJson.unlimited === true,
          resetAt: aiJson.resetAt ?? null,
        };
      }

      let customDomains: CustomDomainsUsage | null = null;
      if (domainsRes && domainsRes.ok) {
        const dJson = (await domainsRes.json()) as CustomDomainsResponse;
        customDomains = {
          used: Number(dJson.used) || 0,
          cap: Number(dJson.cap ?? 0),
        };
      }

      let seats: SeatsUsage | null = null;
      if (teamRes && teamRes.ok) {
        const tJson = (await teamRes.json()) as TeamMembersResponse;
        if (tJson.seats) {
          seats = {
            used: Number(tJson.seats.used) || 0,
            max: Number(tJson.seats.max ?? 0),
          };
        }
      }

      if (!isMountedRef.current) return;

      const baseMaxPages = Number(limits.usage?.maxPages ?? limits.limits?.maxPages ?? 0);
      const pageSlotsExtra = Number(limits.usage?.pageSlotsExtra ?? 0);
      const effectiveMaxPages = Number(
        limits.usage?.maxPagesEffective ?? baseMaxPages + pageSlotsExtra
      );

      const baseStorageCapMb = Number(limits.usage?.storageCapMb ?? -1);
      const storagePackMb = Number(limits.usage?.storagePackMb ?? 0);
      const effectiveStorageCapMb = Number(
        limits.usage?.storageCapMbEffective ??
          (baseStorageCapMb === -1 ? -1 : baseStorageCapMb + storagePackMb)
      );

      setState({
        isLoading: false,
        error: null,
        plan: normalizePlan(limits.plan),
        pages: {
          used: Number(limits.usage?.pages) || 0,
          max: effectiveMaxPages,
          baseMax: baseMaxPages,
          extraFromAddons: pageSlotsExtra,
        },
        storage: {
          usedMb: Number(limits.usage?.storageUsedMb) || 0,
          capMb: effectiveStorageCapMb,
          baseCapMb: baseStorageCapMb,
          packMb: storagePackMb,
        },
        submissions: {
          used: Number(limits.usage?.submissionsUsed) || 0,
          cap: Number(limits.usage?.submissionsCap ?? -1),
        },
        ai,
        customDomains,
        seats,
        addons: {
          pageSlotsExtra,
          domainSlotsExtra: Number(limits.usage?.customDomainSlotsExtra ?? 0),
          storagePackMb,
          aiGenPackRemaining: Number(limits.usage?.aiGenPackRemaining ?? 0),
        },
        retentionDays:
          typeof limits.quotas?.analyticsRetentionDays === 'number'
            ? limits.quotas.analyticsRetentionDays
            : null,
        grandfatheredUntil: limits.grandfatheredUntil ?? null,
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load plan usage';
      setState((s) => ({ ...s, isLoading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchAll();
    const id = window.setInterval(fetchAll, REFRESH_INTERVAL_MS);
    const onFocus = () => {
      fetchAll();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      isMountedRef.current = false;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchAll]);

  return { ...state, refresh: fetchAll };
}
